/**
 * CasePodcastCoverage
 *
 * Shows an "External research coverage" card on a case-detail page when the
 * case has been flagged with `PODCAST_COVERAGE_AVAILABLE` by our multi-
 * podcast RSS indexer (backend/scrapers/podcast_rss_scraper.py). Lists every
 * episode that references the case, grouped by podcast, and — when the
 * show notes contained tipline/contact info — surfaces the extracted
 * phone numbers and email addresses pulled from that episode's description.
 *
 * Data sources on `caseData`:
 *   • flags: ["PODCAST_COVERAGE_AVAILABLE", ...]           (gate)
 *   • podcast_coverage_episodes: [{podcast_key, podcast_name, episode_guid,
 *       episode_title, episode_url, pub_date, matched_person, ...}, ...]
 *   • enrichment_notes: [{source:"podcast_rss_scraper",
 *       dedup_key:"podcast:{key}:{guid}",
 *       structured:{phones:[], emails:[], state_guess, ...}}, ...]
 *   • podcast_coverage (legacy, single Fall Line entry)     (fallback)
 *
 * Visibility: public — shown to all users, not admin-only.
 * Renders nothing when the case lacks the flag and has no coverage data.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Headphones, ExternalLink, Phone, Mail, Calendar,
  AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { clearPodcastReviewFlag } from '@/lib/api';

function formatPubDate(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 16);
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/** Build a map of episode_guid -> extracted structured data from
 *  enrichment_notes so we can surface tipline phones/emails next to
 *  each episode card. */
function buildEnrichmentIndex(notes = []) {
  const idx = {};
  for (const n of notes) {
    if (n?.source !== 'podcast_rss_scraper') continue;
    const guid = n?.structured?.episode_guid;
    if (!guid) continue;
    idx[guid] = n.structured;
  }
  return idx;
}

/** Group episode records by podcast_name, preserving input order within
 *  each group. Deduplicates by episode_guid so multi-part series that
 *  back-reference the same subject don't render the same episode twice. */
function groupByPodcast(episodes = []) {
  const seen = new Set();
  const groups = {};
  const order = [];
  for (const e of episodes) {
    const key = e.episode_guid || e.episode_url || e.episode_title;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const label = e.podcast_name || e.podcast_key || 'Podcast';
    if (!groups[label]) {
      groups[label] = [];
      order.push(label);
    }
    groups[label].push(e);
  }
  // Sort each group's episodes: newest pub_date first, then title
  for (const k of Object.keys(groups)) {
    groups[k].sort((a, b) => {
      const ad = Date.parse(a.pub_date || '') || 0;
      const bd = Date.parse(b.pub_date || '') || 0;
      return bd - ad;
    });
  }
  return { groups, order };
}

export default function CasePodcastCoverage({
  caseData,
  isAdmin = false,
  onFlagCleared,
}) {
  const [clearing, setClearing] = useState(false);

  if (!caseData) return null;

  const flags = Array.isArray(caseData.flags) ? caseData.flags : [];
  const hasFlag = flags.includes('PODCAST_COVERAGE_AVAILABLE');
  const needsReview = flags.includes('PODCAST_MATCH_NEEDS_REVIEW');

  const handleClearReview = async () => {
    if (clearing) return;
    setClearing(true);
    try {
      const res = await clearPodcastReviewFlag(caseData.id);
      toast.success('Review flag cleared', {
        description: `Audit entry recorded at ${new Date(res.cleared_at).toLocaleString()}`,
      });
      if (typeof onFlagCleared === 'function') onFlagCleared();
    } catch (err) {
      console.error('clear podcast review flag failed', err);
      toast.error('Could not clear review flag', {
        description: err?.response?.data?.detail || err.message || 'Request failed',
      });
    } finally {
      setClearing(false);
    }
  };

  // Gather episodes from the new multi-podcast shape + legacy shape
  const episodes = [];
  if (Array.isArray(caseData.podcast_coverage_episodes)) {
    episodes.push(...caseData.podcast_coverage_episodes);
  }
  const legacy = caseData.podcast_coverage;
  if (legacy && legacy.episode_url) {
    episodes.push({
      podcast_key: 'the_fall_line',
      podcast_name: legacy.podcast || 'The Fall Line',
      episode_guid: legacy.episode_url,
      episode_title: legacy.episode_title,
      episode_url: legacy.episode_url,
      pub_date: legacy.pub_date,
    });
    if (Array.isArray(legacy.all_episode_urls)) {
      for (const [i, u] of legacy.all_episode_urls.entries()) {
        if (u === legacy.episode_url) continue;
        episodes.push({
          podcast_key: 'the_fall_line',
          podcast_name: legacy.podcast || 'The Fall Line',
          episode_guid: u,
          episode_title: `${legacy.episode_title || 'Episode'} — Part ${i + 2}`,
          episode_url: u,
        });
      }
    }
  }

  // Gate: only show when the flag is set AND there is at least one episode
  if (!hasFlag || episodes.length === 0) return null;

  const { groups, order } = groupByPodcast(episodes);
  const enrichmentIdx = buildEnrichmentIndex(caseData.enrichment_notes);
  const totalEpisodes = Object.values(groups)
    .reduce((acc, arr) => acc + arr.length, 0);

  return (
    <Card
      data-testid="case-podcast-coverage"
      className="border-purple-200 bg-purple-50/40">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <Headphones className="w-4 h-4 text-purple-700" />
            External research coverage
          </CardTitle>
          <Badge
            variant="outline"
            className="bg-purple-100 text-purple-800 border-purple-300 text-xs shrink-0"
            data-testid="podcast-coverage-episode-count">
            {totalEpisodes} episode{totalEpisodes === 1 ? '' : 's'}
            {order.length > 1 ? ` · ${order.length} podcasts` : ''}
          </Badge>
        </div>

        {needsReview && (
          <div
            className="mt-2 rounded-md border border-amber-300 bg-amber-50
                       p-2.5 text-xs flex items-start gap-2"
            data-testid="podcast-needs-review-banner">
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="text-amber-900 font-medium leading-snug">
                Needs manual review
              </div>
              <div className="text-amber-800 leading-snug">
                The only podcast covering this case is Murder Sheet, which
                discusses both victims and accused perpetrators — verify
                that the match here refers to the missing person before
                treating this coverage as actionable.
              </div>
              {isAdmin && (
                <div className="pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClearReview}
                    disabled={clearing}
                    data-testid="clear-podcast-review-flag-btn"
                    className="h-7 text-[11px] border-amber-400 bg-white
                               text-amber-900 hover:bg-amber-100 gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {clearing ? 'Clearing…' : 'Clear Review Flag'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-4 text-sm">
        {order.map((pod) => (
          <div
            key={pod}
            className="border-l-2 border-purple-300 pl-3 space-y-2"
            data-testid={`podcast-group-${pod.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="font-medium text-sm text-purple-900">
              {pod}
            </div>

            <ul className="space-y-2">
              {groups[pod].map((ep) => {
                const enrich = enrichmentIdx[ep.episode_guid] || {};
                const phones = Array.isArray(enrich.phones) ? enrich.phones : [];
                const emails = Array.isArray(enrich.emails) ? enrich.emails : [];
                const pub = formatPubDate(ep.pub_date);
                return (
                  <li
                    key={ep.episode_guid || ep.episode_url}
                    className="rounded-md border border-purple-100 bg-white/70 p-2.5
                               space-y-1.5"
                    data-testid="podcast-episode-card">
                    <div className="text-slate-800 text-xs font-medium leading-snug">
                      {ep.episode_title || 'Episode'}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
                      {pub && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {pub}
                        </span>
                      )}
                      {ep.episode_url && (
                        <a
                          href={ep.episode_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1
                                     text-purple-800 hover:text-purple-900 underline"
                          data-testid="podcast-coverage-episode-link">
                          Listen <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>

                    {(phones.length > 0 || emails.length > 0) && (
                      <div className="pt-1.5 mt-1.5 border-t border-purple-100
                                      space-y-1 text-[11px]">
                        <div className="text-slate-500 uppercase tracking-wide text-[10px]
                                        font-medium">
                          From show notes
                        </div>
                        {phones.slice(0, 4).map((p) => (
                          <div
                            key={`p-${p}`}
                            className="inline-flex items-center gap-1 text-slate-700 mr-3"
                            data-testid="podcast-episode-phone">
                            <Phone className="w-3 h-3 text-purple-700" />
                            <a
                              href={`tel:${p.replace(/\D/g, '')}`}
                              className="hover:underline">
                              {p}
                            </a>
                          </div>
                        ))}
                        {emails.slice(0, 3).map((em) => (
                          <div
                            key={`e-${em}`}
                            className="inline-flex items-center gap-1 text-slate-700 mr-3"
                            data-testid="podcast-episode-email">
                            <Mail className="w-3 h-3 text-purple-700" />
                            <a
                              href={`mailto:${em}`}
                              className="hover:underline break-all">
                              {em}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div
          className="text-[11px] text-slate-500 pt-2 italic border-t border-purple-100"
          data-testid="podcast-coverage-disclaimer">
          External coverage — Link The Missing is not affiliated with these
          podcasts. Episode links point to third-party platforms; tipline
          phones and contact emails shown above were extracted directly from
          each episode's publicly-available show notes.
        </div>
      </CardContent>
    </Card>
  );
}
